import axios from 'axios';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import { translateErrorMessage } from './errorTranslator';
import { queryClient } from './queryClient';
import { resetAllStores } from './resetStores';
import { getAuthHeader, useAuthStore } from '@/state/authState';
import { MODULE_PAGE_BASE_PATH } from '@/constants/modulePages';
import { isJobsSiteOrigin } from '@/lib/jobsSite';

export interface EmployeeMetrics {
  employeeId: string;
  employeeName: string;
  workedPositions: number;
  addedCandidates: number;
  assignedCandidates: number;
  contactedCandidates: number;
  // Dönemde durumu (aşaması) değiştirilen benzersiz aday sayısı.
  statusChangedCandidates: number;
  // Dönemde dokunulan benzersiz aday birleşimi (eklenen ∪ atanan ∪ iletişim ∪ durum değişikliği); her aday 1 kez.
  totalCandidates: number;
}

export interface EmployeeReportData {
  startDate: string;
  endDate: string;
  rows: EmployeeMetrics[];
  totals: Omit<EmployeeMetrics, 'employeeId' | 'employeeName'>;
}

export interface CandidateDetail {
  candidateId: string;
  candidateName: string;
  candidateTitle: string;
}

export interface ContactedCandidateDetail {
  candidateId: string;
  candidateName: string;
  candidateTitle: string;
  lastTouchedAt: string;
  touchCount: number;
}

export interface PositionActivityDetail {
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  customerName: string | null;
  assignedCandidates: CandidateDetail[];
  contactedCandidates: ContactedCandidateDetail[];
}

export interface EmployeeDetailData {
  employeeId: string;
  employeeName: string;
  startDate: string;
  endDate: string;
  positions: PositionActivityDetail[];
  newCandidates: CandidateDetail[];
}

export interface DayRow {
  date: string;
  employees: EmployeeMetrics[];
}

export interface DailyReportData {
  startDate: string;
  endDate: string;
  days: DayRow[];
}

export interface PivotEmployee {
  employeeId: string;
  employeeName: string;
}

export interface PivotDay {
  date: string;
}

export interface PivotPositionRow {
  positionId: string;
  companyName: string;
  positionTitle: string;
  requestNo: string | null;
  counts: number[][];
  // Her hücre [dayIdx][empIdx] için benzersiz aday indeksleri (aynı aday = aynı indeks).
  // Benzersiz-aday toplamları bu setlerin birleşiminden hesaplanır. counts[d][e] === candidateCells[d][e].length.
  candidateCells: number[][][];
  dayTotals: number[];
  grandTotal: number;
}

export interface PivotReportData {
  startDate: string;
  endDate: string;
  days: PivotDay[];
  employees: PivotEmployee[];
  rows: PivotPositionRow[];
  dayEmployeeTotals: number[][];
  dayTotals: number[];
  grandTotal: number;
}

// ── Pozisyon Süreç Raporu ─────────────────────────────────────────────────────
export type KanbanStageType = 'PENDING' | 'INTERVIEWING' | 'HIRED' | 'REJECTED';
export type ReportGranularity = 'weekly' | 'monthly' | 'yearly';

export interface StageColumn {
  name: string;
  type: KanbanStageType;
}

export interface MetricValue {
  current: number;
  previous: number;
  delta: number;
}

export interface StageMetric extends MetricValue {
  stage: string;
}

export interface PositionProcessSummaryRow {
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  customerName: string | null;
  status: string;
  updatedAt: string;
  reached: number;
  hired: number;
  rejected: number;
}

export interface PositionProcessSummaryData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  rows: PositionProcessSummaryRow[];
  /** Filtreye uyan toplam pozisyon sayısı (rows en fazla ilk 100'ü içerir). */
  totalPositionCount?: number;
  /** Toplam pozisyon sayısı gösterilen satırları aşıyorsa true — özet kartları ilk 100'e dayanır. */
  truncated?: boolean;
}

export interface PositionProcessReportData {
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  customerName: string | null;
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  previousStartDate: string;
  previousEndDate: string;
  stageColumns: StageColumn[];
  reachedCandidates: MetricValue;
  stageMetrics: StageMetric[];
  rejectedCandidates: MetricValue;
  hiredCandidates: MetricValue;
}

// ── Çapraz-pozisyon Aşama Analizi ──────────────────────────────────────────────
export interface StageOption {
  name: string;
  type: KanbanStageType;
  positionCount: number;
}

export interface StageListData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  stages: StageOption[];
}

export interface StageFilterCustomer {
  id: string;
  name: string;
}
export interface StageFilterPosition {
  id: string;
  title: string;
  requestNo: string | null;
  customerCompanyId: string | null;
}
export interface StageFilterEmployee {
  id: string;
  name: string;
}
export interface StageFilterOptionsData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  customers: StageFilterCustomer[];
  positions: StageFilterPosition[];
  employees: StageFilterEmployee[];
}

export interface StagePositionRow {
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  customerName: string | null;
  count: number;
}

export interface StageEmployeeRow {
  employeeId: string | null;
  employeeName: string;
  movedCount: number;
}

export interface StageEmployeeBreakdown {
  positionId: string;
  positionTitle: string;
  employees: StageEmployeeRow[];
}

export interface StageAnalysisRow {
  stageName: string;
  type: KanbanStageType;
  total: number;
  positions: StagePositionRow[];
  employeeBreakdown: StageEmployeeBreakdown[] | null;
}

export interface StageAnalysisData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  stages: StageAnalysisRow[];
  /** positionId → distinct aday sayısı (aşama sayıları toplanmaz). */
  positionDistinctTotals: Record<string, number>;
}

// Pivot görünümü: satır=pozisyon, sütun=aşama. Diziler stageColumns ile aynı sırada.
export interface StagePivotEmployee {
  employeeId: string | null;
  employeeName: string;
  /** stageColumns sırasında: bu çalışanın o aşamaya taşıdığı distinct aday (moved). */
  moved: number[];
  /** moved içinden dönemde ilk kez eklenen (isNew=true) distinct aday. */
  movedNew: number[];
  /** Kırılım aşamaları boyunca distinct aday (aşama sayıları toplanmaz). */
  rowTotal: number;
  rowNewTotal: number;
}
export interface StagePivotPosition {
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  /** null = aşama bu pozisyonun board'unda yok ("-" gösterilir); 0 = var, aday yok. */
  counts: (number | null)[];
  /** counts içinden dönemde ilk kez eklenen (isNew=true) distinct aday. */
  newCounts: number[];
  rowTotal: number;
  rowNewTotal: number;
  employees: StagePivotEmployee[];
}
export interface StagePivotCompany {
  customerCompanyId: string | null;
  customerName: string;
  positions: StagePivotPosition[];
  columnTotals: number[];
  columnNewTotals: number[];
  grandTotal: number;
  grandNewTotal: number;
}
export interface StagePivotData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  stageColumns: string[];
  breakdownStages: string[];
  /** __all__ kırılımı aşama sınırından kırpıldıysa true. */
  breakdownTruncated?: boolean;
  companies: StagePivotCompany[];
  columnTotals: number[];
  columnNewTotals: number[];
  grandTotal: number;
  grandNewTotal: number;
}

// Çalışan-merkezli aşama pivotu: satır=çalışan, sütun=aşama.
export interface EmployeeStageRow {
  employeeId: string | null;
  employeeName: string;
  counts: number[];
  /** Rapor döneminde ilk kez eklenen (isNew=true) distinct aday sayıları — counts ile aynı sıra. */
  newCounts: number[];
  rowTotal: number;
  /** rowTotal içinden dönemde ilk kez eklenen (isNew=true) distinct aday. */
  rowNewTotal: number;
}
export interface EmployeeStagePivotData {
  startDate: string;
  endDate: string;
  granularity: ReportGranularity;
  stageColumns: string[];
  employees: EmployeeStageRow[];
  columnTotals: number[];
  /** Dönemde ilk kez eklenen (isNew=true) sütun toplamları — columnTotals ile aynı sıra. */
  columnNewTotals: number[];
  grandTotal: number;
  /** grandTotal içinden dönemde ilk kez eklenen (isNew=true) distinct aday. */
  grandNewTotal: number;
}

export interface StageCandidateDetailItem {
  candidateId: string;
  candidateName: string;
  /** Adayın LinkedIn profil adresi; boşsa null. */
  linkedinUrl: string | null;
  /** true = o dönemde eklendi (yeşil); false = önceden eklenmiş (sarı). */
  isNew: boolean;
}
export interface StageCandidateDetailsData {
  startDate: string;
  endDate: string;
  /** Grup başına (yeni/eski) en fazla N aday — tam sayılar newCount/oldCount'ta. */
  candidates: StageCandidateDetailItem[];
  newCount: number;
  oldCount: number;
}

export interface CustomerReportFiltersData {
  customers: { id: string; name: string }[];
  positions: { id: string; title: string; requestNo: string | null }[];
  stages: { name: string; type: KanbanStageType }[];
}
export interface CustomerReportRow {
  pipelineId: string;
  candidateId: string;
  candidateName: string;
  linkedinUrl: string | null;
  stageEnteredAt: string | null;
  stageName: string;
  positionId: string;
  positionTitle: string;
  requestNo: string | null;
  /** Pozisyonun müşterisi; yoksa null. */
  customerName: string | null;
  universities: string[];
  yearsOfExperience: number | null;
  assignedByName: string | null;
}
export interface CustomerReportData {
  customerCompanyIds: string[];
  /** Seçili müşterilerin adları (virgülle birleşik). */
  customerName: string | null;
  /** Seçili aşamalar — her satırın kendi aşaması row.stageName'de. */
  stageNames: string[];
  rows: CustomerReportRow[];
  /** Satır sayısı sunucu üst sınırını (5.000) aştıysa true — liste en yeni girişlerle kırpıldı. */
  truncated: boolean;
}

/** Aday Profili raporu — server/src/dtos/candidate-profile-report.dto.ts ile aynı şekil. */
export type CandidateProfileMetricKey = 'university' | 'experience';
export const CANDIDATE_PROFILE_UNSPECIFIED = '__unspecified__';
export interface CandidateProfileBucket {
  key: string;
  label: string;
  count: number;
  percentage: number;
}
export interface CandidateProfileMetric {
  key: CandidateProfileMetricKey;
  total: number;
  buckets: CandidateProfileBucket[];
  multiCount: boolean;
  ordered: boolean;
}
export interface CandidateProfileReportData {
  filters: {
    positionIds: string[];
    customerCompanyIds: string[];
    stages: string[];
    startDate: string | null;
    endDate: string | null;
    sharedOnly: boolean;
    degreeLevels: string[];
  };
  /** Seçili müşteri firmaların adları. */
  customers: { id: string; name: string }[];
  positions: { id: string; title: string; requestNo: string | null; customerName: string | null }[];
  totalCandidates: number;
  metrics: Partial<Record<CandidateProfileMetricKey, CandidateProfileMetric>>;
}
export interface CandidateProfileFiltersData {
  customers: { id: string; name: string }[];
  positions: {
    id: string;
    title: string;
    requestNo: string | null;
    customerCompanyId: string | null;
    status: string;
    /** Pozisyonun aşama (kanban sütun) adları. */
    stages: string[];
  }[];
}
export const resolveBaseUrl = () => {
  const configuredUrl = (import.meta.env.VITE_API_URL || '').trim();
  const defaultUrl = '/api';
  // On the listings' own domain the API is its own /api: candidate sign-in cookies stay on that domain.
  if (!configuredUrl || isJobsSiteOrigin()) return defaultUrl;
  const isBrowser = typeof window !== 'undefined';
  const isSecureContext = isBrowser && window.location.protocol === 'https:';
  const isInsecureConfiguredUrl = configuredUrl.startsWith('http://');
  if (isSecureContext && isInsecureConfiguredUrl) {
    return defaultUrl;
  }
  return configuredUrl;
};
export const API_URL = resolveBaseUrl();
export const api = axios.create({
  baseURL: API_URL,
});

const SILENT_ERROR_MESSAGES = new Set([
  'MESSAGES.FORM360.ASSIGNMENT_NOT_FOUND',
  'MESSAGES.FORM360.ASSIGNMENT_NOT_FOUND_OR_CLOSED',
]);

const RATE_LIMIT_TOAST_ID = 'rate-limit-error';

declare module 'axios' {
  interface AxiosRequestConfig {
    /** Hata interceptor'ı toast göstermesin (çağıran hatayı kendisi gösterir). Ağa gönderilmez. */
    silentError?: boolean;
  }
}

api.interceptors.request.use((config) => {
  // X-Silent-Error yalnızca istemci içi bir bayrak: özel header olarak gitseydi farklı origin'deki API'de
  // CORS preflight'ına takılırdı (sunucunun izinli header listesinde yok). Bayrağa çevrilip header silinir.
  if (config.headers?.['X-Silent-Error'] === 'true') {
    config.silentError = true;
    delete config.headers['X-Silent-Error'];
  }
  if (config.headers) {
    config.headers['ngrok-skip-browser-warning'] = 'true';
    // Sunucuda üretilen Excel/PDF'ler arayüz diliyle insin: çağıran ayrıca vermediyse aktif dili ekle.
    if (!config.headers['Accept-Language'] && i18next.language) {
      config.headers['Accept-Language'] = i18next.language;
    }
  }

  const publicEndpoints = ['/kvkk/validate', '/kvkk/confirm', '/leave-verifications/', '/newsletter/'];
  const isPublicEndpoint = publicEndpoints.some((endpoint) => config.url?.includes(endpoint));
  if (!isPublicEndpoint) {
    const authHeader = getAuthHeader();
    if (authHeader && config.headers) {
      config.headers.Authorization = authHeader;
    }
  }
  return config;
});
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const pathname = window.location.pathname;
    const isAuthPage =
      pathname === '/' ||
      pathname === '/landing' ||
      pathname === '/pricing' ||
      pathname === '/terms-and-conditions' ||
      pathname === '/privacy' ||
      pathname === '/refund' ||
      pathname === '/about' ||
      pathname.startsWith('/auth') ||
      pathname.startsWith('/oauth') ||
      pathname.startsWith('/reset-password') ||
      pathname.startsWith('/new-employee') ||
      pathname.startsWith('/register') ||
      pathname.startsWith('/register-invited') ||
      pathname.startsWith('/kvkk-approval') ||
      pathname.startsWith('/kvkk-onay') ||
      pathname.startsWith('/verify/') ||
      pathname.startsWith(`${MODULE_PAGE_BASE_PATH}/`) ||
      pathname.startsWith('/unsubscribe');
    if (err?.response?.status === 401) {
      if (!isAuthPage) {
        try {
          resetAllStores(queryClient);
          useAuthStore.getState().setToken(null);
          useAuthStore.getState().setUser(null);
          window.location.href = '/';
          return Promise.reject(err);
        } catch (e) {
          console.error('Logout işlemi sırasında hata:', e);
        }
      }
    }
    const responseData = err?.response?.data;
    let rawErrorMessage: string;
    if (typeof responseData?.message === 'string') {
      rawErrorMessage = responseData.message;
    } else if (typeof responseData?.error?.message === 'string') {
      rawErrorMessage = responseData.error.message;
    } else if (typeof err?.message === 'string') {
      rawErrorMessage = err.message;
    } else {
      rawErrorMessage = 'MESSAGES.GENERAL.INTERNAL_SERVER_ERROR';
    }
    const errorParams = responseData?.params || responseData?.error?.params || {};
    const translatedMessage = translateErrorMessage(rawErrorMessage, errorParams);

    const isSilentRequest = err?.config?.silentError === true;
    if (err?.response?.status !== 401 && !isSilentRequest && !SILENT_ERROR_MESSAGES.has(rawErrorMessage)) {
      // A rate-limited burst fails many requests at once; a shared id collapses them into one toast.
      const toastOptions = err?.response?.status === 429 ? { id: RATE_LIMIT_TOAST_ID } : undefined;
      toast.error(translatedMessage, toastOptions);
    }
    return Promise.reject(err);
  }
);

/**
 * Persists the signed-in user's language preference to the backend (User.language), which the
 * transactional-email stack reads as the first link in its language fallback chain. Sent with
 * X-Silent-Error so a background save failure never toasts the user — the UI language has
 * already changed locally regardless. Only the supported languages are accepted server-side.
 */
export const updateMyLanguage = async (language: string): Promise<void> => {
  await api.patch('/profile/me/language', { language }, { headers: { 'X-Silent-Error': 'true' } });
};

export default api;
